import type {
  SchedulingAlternativeHistory,
  SchedulingSearchMode
} from "../../api/statelessClient";

export interface SchedulingFallbackTicket {
  jobId: string;
  accessToken: string;
}

export interface SchedulingJobTicket {
  contractVersion: 1 | 2;
  operation: "LEGACY_GENERATE" | "PRECHECK";
  jobId: string;
  accessToken: string;
  conditionRevision: number;
  timetableRevision: number;
  searchMode: SchedulingSearchMode;
  alternativeHistory?: SchedulingAlternativeHistory;
  submissionConfirmed: boolean;
  fallback?: SchedulingFallbackTicket;
}

export class SchedulingJobTicketStorageError extends Error {
  constructor() {
    super("无法保存排课进度，请检查浏览器是否允许保存本站数据");
    this.name = "SchedulingJobTicketStorageError";
  }
}

export function createSchedulingJobTicket(
  conditionRevision: number,
  timetableRevision: number,
  fallback?: SchedulingFallbackTicket
): SchedulingJobTicket {
  const token = new Uint8Array(32);
  crypto.getRandomValues(token);
  return {
    contractVersion: 2,
    operation: "PRECHECK",
    jobId: crypto.randomUUID(),
    accessToken: base64Url(token),
    conditionRevision,
    timetableRevision,
    searchMode: "STRICT",
    submissionConfirmed: false,
    ...(fallback ? { fallback } : {})
  };
}

export function readSchedulingJobTicket(
  projectId: string
): SchedulingJobTicket | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(storageKey(projectId));
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<SchedulingJobTicket>;
    const legacyConfirmed =
      value.contractVersion === undefined &&
      value.operation === undefined &&
      value.submissionConfirmed === true;
    if (legacyConfirmed) {
      value.contractVersion = 1;
      value.operation = "LEGACY_GENERATE";
    }
    if (
      (value.contractVersion !== 1 && value.contractVersion !== 2) ||
      (value.operation !== "PRECHECK" && value.operation !== "LEGACY_GENERATE") ||
      typeof value.jobId !== "string" ||
      !/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(value.jobId) ||
      typeof value.accessToken !== "string" ||
      value.accessToken.length < 32 ||
      !Number.isInteger(value.conditionRevision) ||
      (value.conditionRevision ?? -1) < 0 ||
      !Number.isInteger(value.timetableRevision) ||
      (value.timetableRevision ?? -1) < 0 ||
      (value.searchMode !== "STRICT" &&
        value.searchMode !== "STRICT_THEN_MINIMAL_RELAXATION") ||
      typeof value.submissionConfirmed !== "boolean"
    ) {
      throw new Error("invalid ticket");
    }
    if (
      value.alternativeHistory !== undefined &&
      (typeof value.alternativeHistory !== "object" ||
        value.alternativeHistory === null ||
        Array.isArray(value.alternativeHistory))
    ) {
      throw new Error("invalid alternative history");
    }
    if (
      value.fallback !== undefined &&
      (typeof value.fallback !== "object" ||
        typeof value.fallback.jobId !== "string" ||
        typeof value.fallback.accessToken !== "string")
    ) {
      throw new Error("invalid fallback ticket");
    }
    return value as SchedulingJobTicket;
  } catch {
    clearSchedulingJobTicket(projectId);
    return null;
  }
}

export function writeSchedulingJobTicket(
  projectId: string,
  ticket: SchedulingJobTicket
): void {
  try {
    localStorage.setItem(storageKey(projectId), JSON.stringify(ticket));
  } catch {
    throw new SchedulingJobTicketStorageError();
  }
}

export function clearSchedulingJobTicket(
  projectId: string,
  expectedJobId?: string
): void {
  try {
    if (expectedJobId) {
      const current = readSchedulingJobTicketWithoutCleanup(projectId);
      if (current?.jobId !== expectedJobId) return;
    }
    localStorage.removeItem(storageKey(projectId));
  } catch {
    // A completed server job is still protected by its seven-day expiry.
  }
}

function readSchedulingJobTicketWithoutCleanup(
  projectId: string
): SchedulingJobTicket | null {
  const raw = localStorage.getItem(storageKey(projectId));
  return raw ? (JSON.parse(raw) as SchedulingJobTicket) : null;
}

function storageKey(projectId: string): string {
  return `sped-scheduler:scheduling-job:${projectId}`;
}

function base64Url(value: Uint8Array): string {
  const binary = String.fromCharCode(...value);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}
